import { Collection } from 'firestorter';

import Collections from '../utils/collections';
import { firestorePaths } from '../utils/paths';
import BaseDocument from './BaseDocument';

export const ScheduledAssignmentStatus = {
  PENDING: 'PENDING',
  ASSIGNED: 'ASSIGNED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
  ON_HOLD_FOR_REVIEW: 'ON_HOLD_FOR_REVIEW',
};

export const ScheduledAssignmentType = {
  PROGRAM: 'PROGRAM',
  TRACK: 'TRACK',
  WORKOUT: 'WORKOUT',
};

/**
 * Class representing a scheduled assignment.
 *
 * @class ScheduledAssignment
 * @extends BaseDocument
 */
class ScheduledAssignment extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.SCHEDULED_ASSIGNMENT}/${id}`, opts);
  }

  get userId() {
    return this.data.userId;
  }

  get coachId() {
    return this.data.coachId;
  }

  get type() {
    return this.data.type;
  }

  get status() {
    return this.data.status;
  }

  get scheduledFor() {
    return this.data.scheduledFor;
  }

  get displayName() {
    return this.data.displayName;
  }

  get productName() {
    return this.data.productName || '';
  }

  get payload() {
    return this.data.payload;
  }

  /**
   * Get the workout edits the coach made to this assignment before it fired,
   * keyed by the `YYYY-MM-DD` date the edited workout lands on. Each entry is
   * `{ workoutId, workout }`: the workout the edit was made against, and the
   * coach's content for it. The processor swaps the content in for the source
   * workout's own when the assignment fires — but only where the workout still
   * scheduled on that date is the one the edit was made against.
   * @return {Object} The overrides, or an empty object when there are none.
   */
  get workoutOverrides() {
    return this.data.payload?.workoutOverrides || {};
  }

  /**
   * Store the coach's version of the workout this assignment will write on a date,
   * to be swapped in for the source workout's own content when the assignment
   * fires. The source workout is left untouched, so the same workout assigned to
   * other clients is unaffected.
   *
   * Written as a single field path so a concurrent edit to another date survives.
   * Security rules only allow this while the assignment is still PENDING, and only
   * when `workoutOverrides` is the one key of the payload that changes.
   *
   * Stored with the workout the edit was made against: a date is a slot in the
   * schedule, not a workout, so a schedule that shifts before the assignment
   * fires can leave a different workout on the edited date. The processor
   * applies the edit only when the two still match, and reports it as dropped
   * otherwise, rather than pasting one workout's content onto another.
   *
   * @param {string} date - The `YYYY-MM-DD` date the edited workout lands on.
   * @param {string} workoutId - The workout the edit was made against.
   * @param {Object} workoutContent - The workout content to assign on that date.
   * @return {Promise<void>}
   */
  async setWorkoutOverride(date, workoutId, workoutContent) {
    await this.ref.update({
      [`payload.workoutOverrides.${date}`]: { workoutId, workout: workoutContent },
    });
  }

  get createdAt() {
    return this.data.createdAt;
  }

  get onHoldWorkoutReviewId() {
    return this.data.onHoldWorkoutReviewId;
  }

  get isOnHold() {
    return this.data.status === ScheduledAssignmentStatus.ON_HOLD_FOR_REVIEW;
  }

  /**
   * Get unresolved scheduled assignments for a user — PENDING (not fired yet)
   * or ON_HOLD_FOR_REVIEW (workouts not written, waiting on AC review). Both
   * are displayed in the workout calendar banner since neither has actually
   * produced workouts yet.
   * @param {string} userId - The user ID.
   * @param {string} coachId - The coach whose queued work to read; a client can hold more than one.
   * @return {Promise<Collection>} The collection of unresolved scheduled assignments.
   */
  static async getPendingByUser(userId, coachId) {
    const collection = new Collection(firestorePaths.SCHEDULED_ASSIGNMENT, {
      query: (ref) => ref
        .where('userId', '==', userId)
        .where('coachId', '==', coachId)
        .where('status', 'in', [
          ScheduledAssignmentStatus.PENDING,
          ScheduledAssignmentStatus.ON_HOLD_FOR_REVIEW,
        ]),
      createDocument: ({ id }, options) => new ScheduledAssignment(id, options),
    });
    await ScheduledAssignment.initCollection(collection);
    return collection;
  }
}

export default ScheduledAssignment;
